import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FiLifeBuoy } from 'react-icons/fi';
import api from '../config/api';
import { formatLocalDateTime } from '../utils/formatTimestamp';
import './ProfileSupportTickets.css';

/**
 * Top support tickets for a person (by email). Shows up to 5 newest.
 */
export default function ProfileSupportTickets({
  email,
  title = 'Support tickets',
  entityType = null,
  entityId = null,
  limit = 5,
}) {
  const normalized = String(email || '').trim().toLowerCase();
  const enabled = Boolean(normalized);

  const { data, isLoading, isError, error } = useQuery(
    ['profile-support-tickets', normalized, entityType, entityId, limit],
    () => {
      const params = new URLSearchParams();
      params.set('requester_email', normalized);
      params.set('limit', String(limit));
      params.set('page', '1');
      return api.get(`/support/tickets?${params.toString()}`).then((r) => r.data);
    },
    { enabled, staleTime: 20_000, refetchOnWindowFocus: false }
  );

  const tickets = Array.isArray(data?.tickets)
    ? data.tickets
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];

  if (!enabled) return null;

  return (
    <div className="candidate-section candidate-details-card profile-support-tickets">
      <div className="section-header">
        <h2>
          <FiLifeBuoy aria-hidden /> {title}
        </h2>
        <Link
          to={`/support/new?email=${encodeURIComponent(normalized)}`}
          className="btn btn-secondary btn-sm"
        >
          New ticket
        </Link>
      </div>

      {isLoading && <p className="form-hint">Loading tickets…</p>}
      {isError && (
        <p className="error">
          {error?.response?.data?.error || error?.message || 'Could not load support tickets'}
        </p>
      )}

      {!isLoading && !isError && tickets.length === 0 && (
        <p className="profile-empty-state">
          No support tickets for <strong>{normalized}</strong> yet.
        </p>
      )}

      {!isLoading && tickets.length > 0 && (
        <ul className="profile-support-list">
          {tickets.slice(0, limit).map((t) => (
            <li key={t.id} className="profile-support-item">
              <div className="profile-support-item-main">
                <Link to={`/support/${t.id}`} className="profile-support-link">
                  {t.ticket_number || `#${t.id}`}
                </Link>
                <span className={`profile-support-status profile-support-status--${t.status}`}>
                  {t.status}
                </span>
              </div>
              <p className="profile-support-subject">{t.subject || '—'}</p>
              <span className="profile-support-meta">
                {formatLocalDateTime(t.last_message_at || t.createdon_timestamp || t.created_at)}
                {t.priority ? ` · ${t.priority}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {tickets.length > 0 && (
        <p className="form-hint profile-support-footnote">
          Showing up to {limit} most recent tickets linked by email.
          {' '}
          <Link to={`/support?search=${encodeURIComponent(normalized)}`}>
            View all in Support
          </Link>
        </p>
      )}
    </div>
  );
}
